import { useCallback, useEffect, useState } from "react";
import { Loader2, ChevronDown, ChevronUp, AlertCircle, Check, MessageSquarePlus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const when = (s) => new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const Session = ({ s }) => {
  const [open, setOpen] = useState(false);
  const first = s.messages.find((m) => m.role === "user")?.text || "—";
  return (
    <div className="border-t border-slate-100" data-testid={`sweta-session-${s.session_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#F7FAFC]" data-testid={`sweta-session-toggle-${s.session_id}`}>
        <span className="text-xs text-slate-500 w-36 shrink-0">{when(s.last_at)}</span>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#E0F4F6] text-[#007A8A] shrink-0">{s.topic}</span>
        <span className="flex-1 truncate text-sm">{first}</span>
        <span className="text-xs text-slate-500 shrink-0">{s.questions} Q</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-2" data-testid={`sweta-transcript-${s.session_id}`}>{s.messages.map((m, i) => (
        <div key={i} className={`rounded-xl px-3 py-2 text-sm whitespace-pre-line max-w-[80%] ${m.role === "user" ? `bg-[#007A8A] text-white ml-auto ${m.unanswered ? "ring-2 ring-[#FFD166]" : ""}` : "bg-[#F0F4F8]"}`}>{m.text}</div>))}</div>}
    </div>
  );
};

const SaveAnswer = ({ q, auth, onErr, reload }) => {
  const [text, setText] = useState("");
  const [drafting, setDrafting] = useState(false);
  const draft = () => { setDrafting(true); api.post(`/sweta/admin/suggest/${q.id}`, {}, auth).then((r) => setText(r.data.draft)).catch(onErr).finally(() => setDrafting(false)); };
  useEffect(draft, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = () => api.post("/sweta/admin/answers", { question_id: q.id, answer: text }, auth)
    .then(() => { toast.success("Saved — Sweta will use this answer next time"); reload(); }).catch(onErr);
  return (
    <div className="mt-2 flex gap-2" data-testid={`sweta-answer-form-${q.id}`}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1000} disabled={drafting} placeholder={drafting ? "Sweta is drafting an answer…" : "Type the correct answer for Sweta to use…"} className="text-sm" data-testid={`sweta-answer-input-${q.id}`} />
      <div className="flex flex-col gap-2 self-end">
        <Button size="sm" variant="outline" disabled={drafting} onClick={draft} data-testid={`sweta-answer-redraft-${q.id}`}>{drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" />Redraft</>}</Button>
        <Button size="sm" disabled={drafting || text.trim().length < 3 || text.includes("[STAFF:")} onClick={save} className="bg-[#007A8A] text-white" data-testid={`sweta-answer-save-${q.id}`}>Approve &amp; save</Button>
      </div>
    </div>
  );
};

const SavedAnswers = ({ auth, onErr, list, reload }) => {
  const del = (id) => api.delete(`/sweta/admin/answers/${id}`, auth).then(() => { toast.success("Answer removed"); reload(); }).catch(onErr);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="sweta-saved-answers">
      <h3 className="font-bold text-[#0A192F]">Sweta's saved answers <span className="text-xs font-normal text-slate-500">({list.length})</span></h3>
      {list.length ? <ul className="mt-3 divide-y divide-slate-100">{list.map((a) => (
        <li key={a.id} className="py-2 flex gap-3 items-start" data-testid={`sweta-saved-${a.id}`}>
          <div className="flex-1 text-sm"><b>Q:</b> {a.question}<br /><span className="text-slate-600"><b>A:</b> {a.answer}</span></div>
          <Button size="sm" variant="ghost" onClick={() => del(a.id)} data-testid={`sweta-saved-delete-${a.id}`}><Trash2 className="w-4 h-4 text-[#FF2E63]" /></Button>
        </li>))}</ul> : <p className="mt-2 text-sm text-slate-500">No saved answers yet. Add one from the list above.</p>}
    </div>
  );
};

const Unanswered = ({ list, auth, onErr, reload }) => {
  const [open, setOpen] = useState(null);
  const resolve = (id) => api.post(`/sweta/admin/resolve/${id}`, {}, auth).then(() => { toast.success("Marked as followed up"); reload(); }).catch(onErr);
  return (
    <div className="bg-white border border-[#FFD166] rounded-2xl p-5" data-testid="sweta-unanswered">
      <h3 className="font-bold text-[#0A192F] flex items-center gap-2"><AlertCircle className="w-4 h-4 text-[#E07A00]" />Questions Sweta couldn't answer <span className="text-xs font-normal text-slate-500">({list.length} to follow up)</span></h3>
      {list.length ? <ul className="mt-3 divide-y divide-slate-100">{list.map((q) => (
        <li key={q.id} className="py-2" data-testid={`sweta-unanswered-${q.id}`}>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 w-36 shrink-0">{when(q.at)}</span>
            <span className="flex-1 text-sm">{q.text}</span>
            <Button size="sm" variant="outline" onClick={() => setOpen(open === q.id ? null : q.id)} data-testid={`sweta-add-answer-${q.id}`}><MessageSquarePlus className="w-4 h-4 mr-1" />Add answer</Button>
            <Button size="sm" variant="outline" onClick={() => resolve(q.id)} data-testid={`sweta-resolve-${q.id}`}><Check className="w-4 h-4 mr-1" />Followed up</Button>
          </div>
          {open === q.id && <SaveAnswer q={q} auth={auth} onErr={onErr} reload={reload} />}
        </li>))}</ul> : <p className="mt-2 text-sm text-slate-500">All caught up — nothing pending.</p>}
    </div>
  );
};

export const SwetaInbox = ({ auth, onErr }) => {
  const [d, setD] = useState(null);
  const [saved, setSaved] = useState([]);
  const load = useCallback(() => {
    api.get("/sweta/admin/inbox", auth).then((r) => setD(r.data)).catch(onErr);
    api.get("/sweta/admin/answers", auth).then((r) => setSaved(r.data)).catch(onErr);
  }, [auth, onErr]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="w-6 h-6 animate-spin mt-8" />;
  return (
    <div className="mt-6 space-y-5" data-testid="sweta-inbox">
      <Unanswered list={d.unanswered} auth={auth} onErr={onErr} reload={load} />
      <SavedAnswers list={saved} auth={auth} onErr={onErr} reload={load} />
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-bold text-[#0A192F]">Most asked topics</h3>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="sweta-topics">{d.topics.length ? d.topics.map(([t, n]) => (
          <span key={t} className="px-3 py-1.5 rounded-full bg-[#F0F4F8] text-sm" data-testid={`sweta-topic-count-${t}`}><b>{t}</b> · {n}</span>)) : <span className="text-sm text-slate-500">No questions yet</span>}</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 font-bold text-[#0A192F] flex justify-between">Conversations <span className="text-xs font-normal text-slate-500">{d.sessions.length} shown · newest first</span></div>
        {d.sessions.map((s) => <Session key={s.session_id} s={s} />)}
        {!d.sessions.length && <div className="px-4 py-8 text-center text-slate-500 border-t border-slate-100">No Sweta conversations yet</div>}
      </div>
    </div>
  );
};
